// Wire types. These mirror the API's DTOs exactly — see
// v1/api/shared/types.ts, which is the source of truth.

export type ResourceType = 'Equipment' | 'Personnel';

export interface Resource {
  id: number;
  /** Asset tag / natural key. Reservations join on this. */
  title: string;
  name: string;
  type: ResourceType;
  category: string;
  office: string;
  status: string;
  /** YYYY-MM-DD, or '' when not tracked. */
  nextDue: string;
  calStatus: string;
  notes: string;
  /** False = retired. Retired resources are hidden unless `all` is set. */
  isActive: boolean;
}

/** Create/edit payload for the catalogue. Mirrors the API's bodies. */
export interface EquipmentInput {
  title: string;
  name: string;
  type: ResourceType;
  category: string;
  office: string;
  status: string;
  nextDue: string;
  calStatus: string;
  notes: string;
  isActive: boolean;
}

export type ReservationStatus = 'Confirmed' | 'Pending' | 'Active' | 'Canceled';

export interface Reservation {
  id: number;
  reference: string;
  /** Equipment title, not id. */
  equipment: string;
  projectNumber: string;
  projectName: string;
  startDate: string;
  endDate: string;
  status: ReservationStatus;
  reservedBy: string;
  notes: string;
  canceledBy: string;
  cancellationDate: string;
  cancellationReason: string;
}

/** Statuses an existing booking can be moved between in the edit modal. */
export const EDITABLE_STATUSES = ['Confirmed', 'Pending', 'Active'] as const;

/**
 * The fields an existing booking's edit can change. Mirrors the API's
 * PATCH /api/reservations/{id} body.
 *
 * Deliberately excludes the resource and cancellation: `/assign` owns
 * the resource because it has to check bookability, and `/cancel` owns
 * cancelling because it records who and why.
 */
export interface ReservationEdit {
  projectNumber: string;
  projectName: string;
  startDate: string;
  endDate: string;
  reservedBy: string;
  status: (typeof EDITABLE_STATUSES)[number];
  notes: string;
}

/** A file stored against one resource. Mirrors GET /api/equipment/{id}/documents. */
export interface StoredDocument {
  /** Full S3 key. */
  key: string;
  /** Filename, for display. */
  name: string;
  sizeBytes: number;
  /** ISO timestamp, or '' when S3 didn't report one. */
  lastModified: string;
}

/** A presigned POST grant. The browser posts the file straight to S3. */
export interface UploadGrant {
  /** S3 endpoint to POST the multipart form to. */
  url: string;
  /** Policy fields that must be sent before the file part. */
  fields: Record<string, string>;
  /** The key S3 will store it under — chosen by the server, not the client. */
  key: string;
  maxBytes: number;
}

export interface DocumentListing {
  /** `s3://bucket/equipment/12/` — shown so it can be pasted into the S3 console. */
  location: string;
  documents: StoredDocument[];
}

/** One row of the change trail. Mirrors GET /api/audit. */
export interface AuditEntry {
  id: number;
  entityType: 'equipment' | 'reservation' | string;
  entityId: number | null;
  action: string;
  actorUpn: string;
  actorName: string;
  /** Changed fields only — prior values. Null for a create. */
  before: Record<string, unknown> | null;
  /** Changed fields only — new values. */
  after: Record<string, unknown> | null;
  summary: string;
  /** ISO timestamp, UTC. */
  createdAt: string;
}

/** Why the admin decision came out the way it did — see the API's AdminTrace. */
export interface AdminCheck {
  groupConfigured: boolean;
  graphConfigured: boolean;
  graphClientId: string | null;
  userResolved: boolean;
  membershipChecked: boolean;
  schedulerGroupConfigured: boolean;
  error: string | null;
  hint: string | null;
}

export interface CurrentUser {
  oid: string;
  email: string;
  displayName: string;
  /** Member of the Resource Manager admin group — may edit the catalogue. */
  isAdmin: boolean;
  /**
   * May book and edit reservations. Admins always are; so is everyone,
   * until MANAGER_GROUP_OID names a scheduler group.
   */
  isScheduler: boolean;
  adminCheck?: AdminCheck;
}

export type CalibrationStatus = 'Current' | 'Due Soon' | 'Overdue';
export type Availability = 'Available' | 'Reserved' | 'In Use';

export interface EnrichedResource extends Resource {
  cal: CalibrationStatus | null;
  avail: Availability;
}

export interface NewReservation {
  equipment: string;
  projectNumber: string;
  projectName: string;
  startDate: string;
  endDate: string;
  reservedBy: string;
  notes: string;
  status: Exclude<ReservationStatus, 'Canceled'>;
}
